
import { mapState, mapMutations } from 'vuex'

import Icon from '../../common/Icon'

export default {
  name: 'SitePopup',
  components: {
    Icon,
  },
  data() {
    return {
      lastFocusedElement: null,
      focusableElements: [],
      firstFocusableElement: null,
      lastFocusableElement: null,
      // Timeout for Safari focus workaround
      focusTimeout: null,
    }
  },
  computed: {
    ...mapState([ 'showSitePopup', 'sitePopupInfo' ]),
    blok() {
      return this.sitePopupInfo
    },
    img() {
      if (this.blok?.image_url) {
        return this.blok.image_url
      }
      return ''
    }
  },
  watch: {
    showSitePopup: {
      handler(isOpen) {
        if (isOpen) {
          this.$nextTick(() => {
            this.openModal()
          })
        } else {
          this.closeModal()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapMutations([ 'setShowSitePopup', 'setSitePopupInfo']),

    openModal() {
      // Store the currently focused element
      this.lastFocusedElement = document.activeElement

      // Make background inert
      this.makeBackgroundInert(true)

      // Setup focus trap
      this.setupFocusTrap()

      // Focus the first element after a small delay to ensure proper setup
      this.focusTimeout = setTimeout(() => {
        this.focusFirstElement()
      }, 50)
    },

    closeModal() {
      // Clear any pending focus timeout
      if (this.focusTimeout) {
        clearTimeout(this.focusTimeout)
        this.focusTimeout = null
      }

      // Remove inert state from background
      this.makeBackgroundInert(false)

      // Return focus to the previously focused element
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        // Use setTimeout to ensure the modal is hidden before focusing
        setTimeout(() => {
          try {
            this.lastFocusedElement.focus()
          } catch (error) {
            // Fallback if the element is no longer focusable
            document.body.focus()
          }
        }, 10)
      }
    },

    closePopup() {
      this.setShowSitePopup(false)
      this.setSitePopupInfo(null)
    },

    closeOnBackdrop() {
      this.closePopup()
    },

    setupFocusTrap() {
      if (!this.$refs.popupInner) return

      // Comprehensive focusable selectors including Safari compatibility
      const focusableSelectors = [
        'button:not([disabled])',
        '[href]:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"]):not([disabled])',
        'details:not([disabled])',
        'summary:not(:disabled)',
        'iframe:not([disabled])',
        '[contenteditable]:not([contenteditable="false"])'
      ].join(', ')

      // Get all focusable elements
      const allFocusable = Array.from(this.$refs.popupInner.querySelectorAll(focusableSelectors))

      // Filter out elements that aren't actually focusable
      this.focusableElements = allFocusable.filter(el => {
        // Check if element is visible and not disabled
        if (el.offsetParent === null) return false
        if (el.disabled) return false
        if (el.getAttribute('aria-hidden') === 'true') return false

        // Check computed styles
        const style = window.getComputedStyle(el)
        if (style.display === 'none' || style.visibility === 'hidden') return false

        return true
      })

      this.firstFocusableElement = this.focusableElements[0] || null
      this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1] || null
    },

    focusFirstElement() {
      if (this.firstFocusableElement) {
        try {
          this.firstFocusableElement.focus()
        } catch (error) {
          // Fallback: try to focus the popup container itself
          if (this.$refs.popupInner) {
            this.$refs.popupInner.focus()
          }
        }
      } else if (this.$refs.popupInner) {
        // If no focusable elements, focus the container
        this.$refs.popupInner.setAttribute('tabindex', '-1')
        this.$refs.popupInner.focus()
      }
    },

    makeBackgroundInert(inert) {
      if (typeof document === 'undefined') return

      const bodyChildren = Array.from(document.body.children)

      bodyChildren.forEach(child => {
        if (!child.contains(this.$el)) {
          if (inert) {
            // Try modern inert property first
            if ('inert' in child) {
              child.inert = true
            } else {
              // Fallback for browsers without inert support
              child.setAttribute('aria-hidden', 'true')
              child.setAttribute('data-focus-trap-inert', 'true')

              // Find all focusable elements and disable them
              const focusableElements = child.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              )

              focusableElements.forEach(el => {
                if (!el.hasAttribute('data-original-tabindex')) {
                  el.setAttribute('data-original-tabindex', el.getAttribute('tabindex') || '0')
                  el.setAttribute('tabindex', '-1')
                }
              })
            }
          } else {
            // Remove inert state
            if ('inert' in child) {
              child.inert = false
            } else if (child.hasAttribute('data-focus-trap-inert')) {
              child.removeAttribute('aria-hidden')
              child.removeAttribute('data-focus-trap-inert')

              // Restore original tabindex values
              const disabledElements = child.querySelectorAll('[data-original-tabindex]')
              disabledElements.forEach(el => {
                const originalTabindex = el.getAttribute('data-original-tabindex')
                if (originalTabindex === '0') {
                  el.removeAttribute('tabindex')
                } else if (originalTabindex && originalTabindex !== 'null') {
                  el.setAttribute('tabindex', originalTabindex)
                } else {
                  el.removeAttribute('tabindex')
                }
                el.removeAttribute('data-original-tabindex')
              })
            }
          }
        }
      })
    },

    handleKeydown(event) {
      // Handle Escape key
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closePopup()
        return
      }

      // Handle Tab key for focus trapping
      if (event.key === 'Tab') {
        // If no focusable elements, prevent tabbing
        if (this.focusableElements.length === 0) {
          event.preventDefault()
          return
        }

        // If only one focusable element, keep focus on it
        if (this.focusableElements.length === 1) {
          event.preventDefault()
          this.firstFocusableElement.focus()
          return
        }

        // Get currently focused element
        const currentFocusedElement = document.activeElement
        const currentIndex = this.focusableElements.indexOf(currentFocusedElement)

        if (event.shiftKey) {
          // Shift + Tab (backwards)
          if (currentIndex <= 0 || currentFocusedElement === this.firstFocusableElement) {
            event.preventDefault()
            this.lastFocusableElement.focus()
          }
        } else {
          // Tab (forwards)
          if (currentIndex === -1 || currentIndex >= this.focusableElements.length - 1 || currentFocusedElement === this.lastFocusableElement) {
            event.preventDefault()
            this.firstFocusableElement.focus()
          }
        }
      }
    }
  },

  beforeDestroy() {
    // Clean up timeouts
    if (this.focusTimeout) {
      clearTimeout(this.focusTimeout)
    }

    // Ensure background inert state is removed
    if (this.showSitePopup) {
      this.makeBackgroundInert(false)
    }
  }
}
